'use client';

import { useCallback, useMemo, useState } from 'react';

import { SearchApiFacilityExam } from '@/app/(main)/(pages)/_types/search-api-response.type';

export const MAX_SELECTED_EXAMS = 50;

export interface UseLaboratoryExamSelectionReturn {
  selected: SearchApiFacilityExam[];
  selectedIds: string[];
  count: number;
  total: number;
  isFull: boolean;
  isSelected(id: string): boolean;
  toggle(exam: SearchApiFacilityExam): void;
}

function toSelectionMap(exams: SearchApiFacilityExam[]): Map<string, SearchApiFacilityExam> {
  return new Map(exams.slice(0, MAX_SELECTED_EXAMS).map((exam) => [exam.id, exam]));
}

export function useLaboratoryExamSelection(
  initialExams: SearchApiFacilityExam[] = [],
): UseLaboratoryExamSelectionReturn {
  const initialExamsKey = initialExams.map((exam) => exam.id).join(',');

  const [selectedById, setSelectedById] = useState<Map<string, SearchApiFacilityExam>>(() =>
    toSelectionMap(initialExams));
  const [previousInitialExamsKey, setPreviousInitialExamsKey] = useState(initialExamsKey);
  const [hasUserSelection, setHasUserSelection] = useState(false);

  if (initialExamsKey !== previousInitialExamsKey) {
    setPreviousInitialExamsKey(initialExamsKey);

    if (!hasUserSelection) {
      setSelectedById(toSelectionMap(initialExams));
    }
  }

  const toggle = useCallback((exam: SearchApiFacilityExam) => {
    setHasUserSelection(true);

    setSelectedById((current) => {
      const next = new Map(current);

      if (next.has(exam.id)) {
        next.delete(exam.id);

        return next;
      }

      if (next.size >= MAX_SELECTED_EXAMS) {
        return current;
      }

      next.set(exam.id, exam);

      return next;
    });
  }, []);

  const isSelected = useCallback((id: string) => selectedById.has(id), [selectedById]);

  const selected = useMemo(() => Array.from(selectedById.values()), [selectedById]);
  const selectedIds = useMemo(() => selected.map((exam) => exam.id), [selected]);
  const total = useMemo(() => selected.reduce((sum, exam) => sum + (exam.price ?? 0), 0), [selected]);

  return {
    selected,
    selectedIds,
    count: selected.length,
    total,
    isFull: selected.length >= MAX_SELECTED_EXAMS,
    isSelected,
    toggle,
  };
}
